/**
 * Admin Multi-Cloud Image Builder - Main interface for custom image creation across cloud providers
 * Provides architecture-aware package selection and build management for AWS, GCP, and Azure
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Layers, Clock, Sparkles, Monitor, Server, Cloud,
  Play, Pencil, Trash2, RefreshCw, ChevronDown, X,
  AlertCircle, CheckCircle2, FileText, Loader2
} from 'lucide-react';
import AMICoverageTracker from './AMICoverageTracker';
import PackageSearchDialog from './PackageSearchDialog';
import PackageAutocomplete from './PackageAutocomplete';
import { BuildProgressStream } from './BuildProgressStream';
import DeployedImagesTab from './DeployedImagesTab';
import BaseImageDiscoveryTab from './BaseImageDiscoveryTab';

// Types
interface ImageTemplate {
  id?: number;
  name: string;
  description: string;
  architecture: 'x86_64' | 'arm64';
  python_version: string;
  packages: string[];
  gpu_enabled: boolean;
  cuda_version?: string;
  custom_script?: string;
  target_providers: CloudProvider[];
  target_regions: { [provider: string]: string[] };
  propalgos_base_packages: boolean;
  base_image_type?: 'ubuntu' | 'rapids';
  base_image_version?: string;
  disk_size_gb?: number;
  include_cufolio?: boolean;
  min_gpu_memory_gb?: number;
}

interface BuildJob {
  id: number;
  template_id: number;
  template_name: string;
  status: 'PENDING' | 'VALIDATING' | 'AGENTIC_TESTING' | 'BUILDING' | 'COPYING' | 'TESTING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';  // Uppercase to match database
  provider?: CloudProvider;  // Optional - might be in build_config
  build_config?: {
    provider?: string;
    [key: string]: any;
  };
  build_region: string;
  created_at: string;
  build_started_at?: string;
  build_completed_at?: string;
  duration_minutes?: number;
  error_message?: string;
  rca_analysis?: {
    error_type?: string;
    failed_provisioner?: string;
    ai_diagnosis?: string;
    ai_suggested_fix?: string;
    ai_confidence?: number;
    model_used?: string;
    iteration_count?: number;
    fixes_attempted?: Array<{
      fix: string;
      success: boolean;
    }>;
  };
  deployed_images?: Array<{
    provider: string;
    region: string;
    image_id: string;
    image_name: string;
  }>;
  created_by?: string;
  image_id?: string;
  build_logs?: string;
  estimated_duration_minutes?: number;
  priority?: 'low' | 'normal' | 'high';
}

type CloudProvider = 'aws' | 'gcp' | 'azure';

interface ProviderConfig {
  name: string;
  displayName: string;
  regions: string[];
  imageFormat: string;
  gpuSupport: {
    x86_64: boolean;
    arm64: boolean;
  };
  architectureSupport: string[];
}

const PYTHON_VERSIONS = ['3.11', '3.12', '3.13'];
const CUDA_VERSIONS = ['11.8', '12.4', '12.8'];

// Mirrors backend's RAPIDS_COMPATIBILITY_MATRIX in build_config.py
const RAPIDS_COMPATIBILITY: Record<string, { python: string[]; cuda: string[] }> = {
  '24.10': { python: ['3.11', '3.12'], cuda: ['11.8', '12.4', '12.8'] },
  '24.12': { python: ['3.11', '3.12'], cuda: ['12.4'] },
  '25.10': { python: ['3.12', '3.13'], cuda: ['12.4', '12.8'] },
  '25.12': { python: ['3.12', '3.13'], cuda: ['12.4', '12.8'] },
  '26.02': { python: ['3.12', '3.13'], cuda: ['12.8'] },
};

const RAPIDS_VERSIONS = Object.keys(RAPIDS_COMPATIBILITY);

const parseRapidsVersion = (v?: string): string => v ? v.split('-')[0] : '24.10';
const buildBaseImageVersion = (rapids: string, cuda: string, py: string): string =>
  `${rapids}-cuda${cuda}-py${py}`;

// Baseline packages from install-packages.sh — used for dedup warnings
const BASELINE_PACKAGE_NAMES = new Set([
  'numpy', 'scipy', 'pandas', 'numba', 'scikit-learn', 'statsmodels',
  'plotly', 'yfinance', 'backtrader', 'arch-py', 'ipywidgets', 'ipykernel',
  'jupyterlab', 'jupyter-server-proxy', 'pyarrow', 'tqdm', 'ccxt', 'streamlit',
]);

const PROVIDER_CONFIGS: { [key in CloudProvider]: ProviderConfig } = {
  aws: {
    name: 'aws',
    displayName: 'Amazon AWS',
    regions: ['us-east-1', 'us-east-2', 'us-west-2'],
    imageFormat: 'AMI',
    gpuSupport: { x86_64: true, arm64: true },
    architectureSupport: ['x86_64', 'arm64']
  },
  gcp: {
    name: 'gcp',
    displayName: 'Google Cloud',
    regions: ['us-central1', 'us-east1', 'us-east4', 'us-west1', 'us-west2', 'us-west3', 'us-west4'],
    imageFormat: 'Image',
    gpuSupport: { x86_64: true, arm64: false },
    architectureSupport: ['x86_64', 'arm64']
  },
  azure: {
    name: 'azure',
    displayName: 'Microsoft Azure',
    regions: ['eastus', 'eastus2', 'westus', 'westus2', 'westus3', 'centralus'],
    imageFormat: 'VHD',
    gpuSupport: { x86_64: true, arm64: false },
    architectureSupport: ['x86_64']
  }
};

interface CompatibleInstance {
  instance_type: string;
  gpu_model: string;
  gpu_manufacturer: string;
  gpu_count: number;
  gpu_memory_per_device_gb: number;
  total_gpu_memory_gb: number;
  gpu_architecture: string;
  available_regions: string[];
  semantic_category: string;
  compatible: boolean;
  architecture_warning?: string;
}

interface BuildLogsResponse {
  job_id: number;
  status: string;
  error_message?: string | null;
  packer_logs?: string | null;
  packer_log_length?: number;
  vm_test_logs?: string | null;
  ssm_document_output?: string | null;
  ssm_output_length?: number;
  document_name?: string;
  message?: string;
}

export default function AdminAMIBuilder() {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState<CloudProvider>('aws');
  const [templates, setTemplates] = useState<ImageTemplate[]>([]);
  const [buildJobs, setBuildJobs] = useState<BuildJob[]>([]);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [logsJobId, setLogsJobId] = useState<number | null>(null);
  const [buildLogs, setBuildLogs] = useState<BuildLogsResponse | null>(null);
  const [buildProgress, setBuildProgress] = useState<{[key: number]: {stage: string, progress: number, elapsed_minutes: number}}>({});
  const [validationStatus, setValidationStatus] = useState<'none' | 'validating' | 'valid' | 'invalid'>('none');
  const [validationResults, setValidationResults] = useState<any>(null);
  const [currentTemplate, setCurrentTemplate] = useState<ImageTemplate>({
    name: '',
    description: '',
    architecture: 'x86_64',
    python_version: '3.11',
    packages: [],
    gpu_enabled: false,
    propalgos_base_packages: true,
    base_image_type: 'ubuntu',
    base_image_version: undefined,
    disk_size_gb: 100,
    include_cufolio: false,
    target_providers: ['aws', 'gcp'],
    target_regions: {
      aws: ['us-east-1'],
      gcp: ['us-central1']
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPackageSearch, setShowPackageSearch] = useState(false);
  const [compatibleInstances, setCompatibleInstances] = useState<{ [provider: string]: CompatibleInstance[] }>({});
  const [loadingInstances, setLoadingInstances] = useState(false);

  // AI Template Generator state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPythonVersion, setAiPythonVersion] = useState('3.12');
  const [aiGpuEnabled, setAiGpuEnabled] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [aiGeneratedTemplate, setAiGeneratedTemplate] = useState<any>(null);

  // Per-job build tracking (one card per provider/job)
  interface ActiveBuild {
    key: string;           // unique: `${templateId}-${provider}`
    jobId: number;         // 0 = pending (pre-POST)
    templateId: number;
    templateName: string;
    templateType: string;
    provider: CloudProvider;
    startedAt: string;
  }
  const [activeBuilds, setActiveBuilds] = useState<ActiveBuild[]>([]);
  const [collapsedBuilds, setCollapsedBuilds] = useState<Set<string>>(new Set());

  // Ref for auto-scrolling logs to bottom
  const logsContainerRef = useRef<HTMLDivElement>(null);

  // Derived RAPIDS state
  const currentRapidsVersion = useMemo(
    () => currentTemplate.base_image_type === 'rapids'
      ? parseRapidsVersion(currentTemplate.base_image_version)
      : '24.10',
    [currentTemplate.base_image_type, currentTemplate.base_image_version]
  );

  // Filter Python/CUDA versions to compatible options when RAPIDS is selected
  const filteredPythonVersions = useMemo(() => {
    if (currentTemplate.base_image_type !== 'rapids') return PYTHON_VERSIONS;
    const compat = RAPIDS_COMPATIBILITY[currentRapidsVersion];
    if (!compat) return PYTHON_VERSIONS;
    // Include current selection even if unsupported (with label handled in render)
    const versions = [...compat.python];
    if (!versions.includes(currentTemplate.python_version) && PYTHON_VERSIONS.includes(currentTemplate.python_version)) {
      versions.push(currentTemplate.python_version);
    }
    return versions.sort();
  }, [currentTemplate.base_image_type, currentRapidsVersion, currentTemplate.python_version]);

  const filteredCudaVersions = useMemo(() => {
    if (currentTemplate.base_image_type !== 'rapids') return CUDA_VERSIONS;
    const compat = RAPIDS_COMPATIBILITY[currentRapidsVersion];
    if (!compat) return CUDA_VERSIONS;
    const versions = [...compat.cuda];
    if (currentTemplate.cuda_version && !versions.includes(currentTemplate.cuda_version) && CUDA_VERSIONS.includes(currentTemplate.cuda_version)) {
      versions.push(currentTemplate.cuda_version);
    }
    return versions.sort();
  }, [currentTemplate.base_image_type, currentRapidsVersion, currentTemplate.cuda_version]);

  // Load data on component mount
  useEffect(() => {
    loadTemplates();
    loadBuildJobs();
  }, []);

  const hasLogContent =
    !!buildLogs &&
    !!(
      (buildLogs.packer_logs && buildLogs.packer_logs.length > 0) ||
      (buildLogs.vm_test_logs && buildLogs.vm_test_logs.length > 0) ||
      (buildLogs.ssm_document_output && buildLogs.ssm_document_output.length > 0) ||
      (buildLogs.error_message && buildLogs.error_message.length > 0)
    );

  const combinedLogs = buildLogs
    ? [
        buildLogs.error_message ? `=== Error Details ===\n${buildLogs.error_message}` : null,
        buildLogs.packer_logs ? `=== Packer Build Logs ===\n${buildLogs.packer_logs}` : null,
        buildLogs.vm_test_logs ? `=== VM Test Logs ===\n${buildLogs.vm_test_logs}` : null,
        buildLogs.ssm_document_output
          ? `=== SSM Document Output${buildLogs.document_name ? ` (${buildLogs.document_name})` : ''} ===\n${buildLogs.ssm_document_output}`
          : null,
        !hasLogContent && buildLogs.message ? buildLogs.message : null
      ]
        .filter((section): section is string => Boolean(section))
        .join('\n\n')
    : '';

  // Auto-refresh logs when modal is open and job is still in progress
  useEffect(() => {
    if (logsJobId === null) return;

    const job = buildJobs.find(j => j.id === logsJobId);
    const isInProgress = job && ['BUILDING', 'VALIDATING', 'AGENTIC_TESTING', 'COPYING', 'TESTING', 'PENDING'].includes(job.status);

    if (!isInProgress) return;

    const interval = setInterval(() => {
      loadBuildLogs(logsJobId);
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [logsJobId, buildJobs]);

  // Scroll to bottom when logs update
  useEffect(() => {
    if (logsContainerRef.current && buildLogs) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [buildLogs]);

  const renderLogSection = (title: string, content?: string | null) => {
    if (!content) return null;
    return (
      <section className="space-y-2 text-left">
        <h4 className="text-sm font-semibold text-blue-300">{title}</h4>
        <pre className="text-left text-xs font-mono text-gray-300 bg-gray-900 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
          {content}
        </pre>
      </section>
    );
  };

  // Auto-restore active builds from DB on page load (one card per job)
  useEffect(() => {
    const runningJobs = buildJobs.filter(j =>
      j.status === 'BUILDING' || j.status === 'AGENTIC_TESTING' || j.status === 'VALIDATING'
    );

    if (runningJobs.length === 0) return;

    setActiveBuilds(prev => {
      const existingKeys = new Set(prev.map(b => b.key));
      const newBuilds: ActiveBuild[] = [];

      for (const job of runningJobs) {
        const provider = (job.build_config?.provider || job.provider || 'aws') as CloudProvider;
        const key = `${job.template_id}-${provider}`;
        if (existingKeys.has(key)) continue;

        const template = templates.find(t => t.id === job.template_id);

        newBuilds.push({
          key,
          jobId: job.id,
          templateId: job.template_id,
          templateName: template?.name || job.template_name || `Template ${job.template_id}`,
          templateType: template?.base_image_type || 'ubuntu',
          provider,
          startedAt: job.created_at || new Date().toISOString(),
        });
      }

      return newBuilds.length > 0 ? [...prev, ...newBuilds] : prev;
    });
  }, [buildJobs, templates]);

  // Auto-refresh progress for active builds
  useEffect(() => {
    const activeBuildIds = buildJobs.filter(j => j.status === 'BUILDING').map(j => j.id);

    if (activeBuildIds.length === 0) return;

    const fetchProgress = async () => {
      const token = localStorage.getItem('authToken');
      for (const jobId of activeBuildIds) {
        try {
          const response = await axios.get(
            `${getApiUrl()}/api/v1/multi-cloud/images/build-jobs/${jobId}/progress`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBuildProgress(prev => {
            const incoming = response.data;
            const existing = prev[jobId];
            // Monotonic: never let progress go backwards
            if (existing && incoming.progress < existing.progress) {
              return { ...prev, [jobId]: { ...incoming, progress: existing.progress, stage: existing.stage } };
            }
            return { ...prev, [jobId]: incoming };
          });
        } catch (err) {
          console.error(`Failed to get progress for job ${jobId}:`, err);
        }
      }
    };

    // Initial fetch
    fetchProgress();

    // Poll every 10 seconds
    const interval = setInterval(fetchProgress, 10000);

    // Refresh full job list every minute to catch completion
    const refreshInterval = setInterval(loadBuildJobs, 60000);

    return () => {
      clearInterval(interval);
      clearInterval(refreshInterval);
    };
  }, [buildJobs]);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${getApiUrl()}/api/v1/multi-cloud/images/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const knownProviders = new Set(Object.keys(PROVIDER_CONFIGS));
      const validBaseTypes = new Set(['ubuntu', 'rapids']);
      const cleaned = (response.data.templates || []).map((t: ImageTemplate) => {
        let baseType = t.base_image_type || 'ubuntu';
        if (!validBaseTypes.has(baseType)) {
          console.warn(`Template "${t.name}" has unsupported base_image_type "${baseType}", normalizing to ubuntu`);
          baseType = 'ubuntu' as const;
        }
        return {
          ...t,
          base_image_type: baseType as 'ubuntu' | 'rapids',
          target_providers: (t.target_providers || []).filter(p => knownProviders.has(p)),
        };
      });
      setTemplates(cleaned);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const loadBuildJobs = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${getApiUrl()}/api/v1/multi-cloud/images/build-jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuildJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to load build jobs:', err);
    }
  };

  const loadBuildLogs = async (jobId: number) => {
    try {
      const token = localStorage.getItem('authToken');
      setLogsJobId(jobId);
      setBuildLogs(null);
      const response = await axios.get<BuildLogsResponse>(`${getApiUrl()}/api/v1/multi-cloud/images/build-jobs/${jobId}/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuildLogs(response.data || null);
    } catch (err: any) {
      console.error('Failed to load build logs:', err);
      setBuildLogs({
        job_id: jobId,
        status: 'ERROR',
        message: err.response?.data?.detail || 'Failed to load logs'
      });
    }
  };

  const handleCancelJob = async (jobId: number, templateId: number) => {
    if (!window.confirm('Cancel this build? All cloud resources will be cleaned up.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${getApiUrl()}/api/v1/multi-cloud/images/templates/${templateId}/build-action`,
        { action: 'cancel', job_ids: [jobId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Refresh job list
        loadBuildJobs();
        alert(`Build cancelled. ${response.data.message}`);
      } else {
        alert('Failed to cancel build: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err: any) {
      console.error('Failed to cancel build:', err);
      alert('Failed to cancel build: ' + (err.response?.data?.detail || err.message));
    }
  };

  const loadCompatibleInstances = useCallback(async () => {
    if (currentTemplate.target_providers.length === 0 || !currentTemplate.gpu_enabled) {
      setCompatibleInstances({});
      return;
    }

    setLoadingInstances(true);
    const newInstances: { [provider: string]: CompatibleInstance[] } = {};

    try {
      const token = localStorage.getItem('authToken');

      // Fetch compatible instances for each selected provider
      await Promise.all(
        currentTemplate.target_providers.map(async (provider) => {
          const regions = currentTemplate.target_regions[provider] || [];
          if (regions.length === 0) return;

          try {
            const params = new URLSearchParams({
              provider,
              architecture: currentTemplate.architecture,
              gpu_enabled: 'true',
              ...regions.reduce((acc, region, idx) => {
                acc[`regions[${idx}]`] = region;
                return acc;
              }, {} as Record<string, string>)
            });

            const response = await axios.get(
              `${getApiUrl()}/api/v1/multi-cloud/images/compatible-instances?${params.toString()}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.compatible_instances) {
              newInstances[provider] = response.data.compatible_instances;
            }
          } catch (err) {
            console.error(`Failed to load compatible instances for ${provider}:`, err);
          }
        })
      );

      setCompatibleInstances(newInstances);
    } catch (err) {
      console.error('Failed to load compatible instances:', err);
    } finally {
      setLoadingInstances(false);
    }
  }, [currentTemplate.target_providers, currentTemplate.target_regions, currentTemplate.architecture, currentTemplate.gpu_enabled]);

  // Load compatible instances when relevant fields change
  useEffect(() => {
    loadCompatibleInstances();
  }, [loadCompatibleInstances]);

  // Auto-validate packages when they change (debounced)
  useEffect(() => {
    if (currentTemplate.packages.length === 0) {
      setValidationStatus('none');
      setValidationResults(null);
      return;
    }

    // Reset validation status when packages/Python version/GPU changes
    setValidationStatus('none');

    // Debounce: wait 2 seconds after last change before validating
    const timer = setTimeout(() => {
      handleValidatePackages();
    }, 2000);

    return () => clearTimeout(timer);
  }, [currentTemplate.packages, currentTemplate.python_version, currentTemplate.gpu_enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleValidatePackages = async () => {
    if (currentTemplate.packages.length === 0) {
      setValidationStatus('valid');
      setSuccess('No packages to validate');
      return;
    }

    setValidationStatus('validating');
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${getApiUrl()}/api/v1/multi-cloud/images/templates/validate-packages-full`,
        {
          packages: currentTemplate.packages,
          python_version: currentTemplate.python_version,
          gpu_enabled: currentTemplate.gpu_enabled,
          base_image_type: currentTemplate.base_image_type || 'ubuntu'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setValidationResults(response.data);

      if (response.data.valid) {
        setValidationStatus('valid');
        setSuccess(`All ${response.data.total_requested} packages validated! Resolved to ${response.data.total_resolved} total packages.`);
      } else {
        setValidationStatus('invalid');
        setError('Package validation failed - see details below');
      }
    } catch (err: any) {
      setValidationStatus('invalid');
      setError(err.response?.data?.detail || 'Validation failed');
    }
  };

  const handleGenerateTemplateWithAI = async () => {
    if (!aiPrompt.trim()) {
      setError('Please enter a description for AI to generate template');
      return;
    }

    setGenerating(true);
    setError(null);
    setAiGeneratedTemplate(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${getApiUrl()}/api/v1/multi-cloud/images/templates/generate-from-description`,
        {
          description: aiPrompt,
          python_version: aiPythonVersion,
          gpu_enabled: aiGpuEnabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAiGeneratedTemplate(response.data.template);
      setSuccess('AI template generated successfully! Click "Use This Template" to populate the form.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'AI generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleUseAITemplate = () => {
    if (!aiGeneratedTemplate) return;

    // Populate form with AI-generated template
    setCurrentTemplate(prev => ({
      ...prev,
      name: aiGeneratedTemplate.name,
      description: aiGeneratedTemplate.description,
      packages: aiGeneratedTemplate.packages,
      python_version: aiPythonVersion,
      gpu_enabled: aiGpuEnabled,
      cuda_version: aiGpuEnabled ? '12.4' : undefined,
      base_image_type: (aiGeneratedTemplate.base_image_type === 'rapids' ? 'rapids' : 'ubuntu') as 'ubuntu' | 'rapids',
      base_image_version: aiGeneratedTemplate.base_image_version
    }));

    // Clear AI state
    setAiPrompt('');
    setAiGeneratedTemplate(null);

    // Validation will auto-trigger via useEffect
    setValidationStatus('none');
    const resolvedBase = aiGeneratedTemplate.base_image_type === 'rapids' ? 'rapids' : 'ubuntu';
    const baseMsg = resolvedBase !== 'ubuntu'
      ? ` Using ${resolvedBase} base image.`
      : '';
    setSuccess(`Template populated!${baseMsg} Validation will run automatically in 2 seconds.`);
  };

  const handleDeleteTemplate = async (templateId: number, templateName: string) => {
    if (!window.confirm(`Delete template "${templateName}"? This cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(
        `${getApiUrl()}/api/v1/multi-cloud/images/templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Template "${templateName}" deleted successfully`);
      loadTemplates();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!currentTemplate.name || !currentTemplate.description) {
      setError('Template name and description are required');
      return;
    }

    if (currentTemplate.packages.length > 0 && validationStatus !== 'valid') {
      setError('Please validate packages before saving template');
      return;
    }

    if (currentTemplate.target_providers.length === 0) {
      setError('At least one target provider is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${getApiUrl()}/api/v1/multi-cloud/images/templates`, currentTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Template saved successfully');
      loadTemplates();
      resetCurrentTemplate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleStartBuild = async (template: ImageTemplate) => {
    if (!template.id || !template.target_providers?.length) return;

    // Prevent duplicate builds for the same template
    if (activeBuilds.some(b => b.templateId === template.id)) return;

    const templateId = template.id;
    const now = new Date().toISOString();

    // Show one build card per provider immediately (pending state)
    const pendingBuilds: ActiveBuild[] = template.target_providers.map(provider => ({
      key: `${templateId}-${provider}`,
      jobId: 0,
      templateId,
      templateName: template.name,
      templateType: template.base_image_type || 'ubuntu',
      provider,
      startedAt: now,
    }));
    setActiveBuilds(prev => [...prev, ...pendingBuilds]);
    setActiveTab(1);  // Navigate to Build Jobs tab

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      // POST returns instantly — background pipeline handles heavy work
      const response = await axios.post(
        `${getApiUrl()}/api/v1/multi-cloud/images/templates/${templateId}/build`,
        {
          providers: template.target_providers,
          run_vm_testing: true,
          skip_on_test_failure: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const result = response.data;
      const returnedJobs: Array<{ id: number; provider?: string; build_config?: { provider?: string } }> =
        result.build_jobs || [];

      // Update each pending build with its real job ID
      setActiveBuilds(prev => prev.map(b => {
        if (b.templateId !== templateId || b.jobId !== 0) return b;
        const matchingJob = returnedJobs.find(j =>
          (j.build_config?.provider || j.provider) === b.provider
        );
        return matchingJob ? { ...b, jobId: matchingJob.id } : b;
      }));

      setSuccess(`Build pipeline started for ${template.name}`);
      loadBuildJobs();

    } catch (err: any) {
      // Remove all pending builds for this template on failure
      setActiveBuilds(prev => prev.filter(b => b.templateId !== templateId));
      setError(err.response?.data?.detail || err.response?.data?.message || 'Failed to start build');
    }
  };

  const dismissBuild = (key: string) => {
    setActiveBuilds(prev => prev.filter(b => b.key !== key));
    setCollapsedBuilds(prev => { const next = new Set(prev); next.delete(key); return next; });
  };

  const toggleCollapseBuild = (key: string) => {
    setCollapsedBuilds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const resetCurrentTemplate = () => {
    setCurrentTemplate({
      name: '',
      description: '',
      architecture: 'x86_64',
      python_version: '3.11',
      packages: [],
      gpu_enabled: false,
      propalgos_base_packages: true,
      base_image_type: 'ubuntu',
      base_image_version: undefined,
      disk_size_gb: 100,
      include_cufolio: false,
      target_providers: ['aws', 'gcp'],
      target_regions: {
        aws: ['us-east-1'],
        gcp: ['us-central1']
      }
    });
  };

  const handleAddPackage = (packageName: string) => {
    if (!currentTemplate.packages.includes(packageName)) {
      setCurrentTemplate(prev => ({
        ...prev,
        packages: [...prev.packages, packageName]
      }));
    }
  };

  const handleRemovePackage = (index: number) => {
    setCurrentTemplate(prev => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== index)
    }));
  };

  const handleArchitectureChange = (arch: 'x86_64' | 'arm64') => {
    const hasGpuSupport = currentTemplate.target_providers.some(provider =>
      PROVIDER_CONFIGS[provider].gpuSupport[arch]
    );

    setCurrentTemplate(prev => ({
      ...prev,
      architecture: arch,
      // Reset GPU settings if no providers support GPU for this architecture
      gpu_enabled: hasGpuSupport ? prev.gpu_enabled : false,
      cuda_version: hasGpuSupport && prev.gpu_enabled ? prev.cuda_version : undefined,
      // Filter providers that don't support this architecture
      target_providers: prev.target_providers.filter(provider =>
        PROVIDER_CONFIGS[provider].architectureSupport.includes(arch)
      ),
      // Update target regions to only include supported providers
      target_regions: Object.fromEntries(
        Object.entries(prev.target_regions).filter(([provider]) =>
          PROVIDER_CONFIGS[provider as CloudProvider].architectureSupport.includes(arch)
        )
      )
    }));
  };

  const handleGpuChange = (enabled: boolean) => {
    setCurrentTemplate(prev => ({
      ...prev,
      gpu_enabled: enabled,
      cuda_version: enabled ? '12.4' : undefined
    }));
  };

  const handleProviderChange = (provider: CloudProvider, enabled: boolean) => {
    setCurrentTemplate(prev => {
      const newProviders = enabled
        ? [...prev.target_providers, provider]
        : prev.target_providers.filter(p => p !== provider);

      const newRegions = { ...prev.target_regions };
      if (enabled) {
        newRegions[provider] = [PROVIDER_CONFIGS[provider].regions[0]];
      } else {
        delete newRegions[provider];
      }

      return {
        ...prev,
        target_providers: newProviders,
        target_regions: newRegions
      };
    });
  };

  const canEnableGpu = () => {
    return currentTemplate.target_providers.some(provider =>
      PROVIDER_CONFIGS[provider].gpuSupport[currentTemplate.architecture]
    );
  };

  const getSupportedProviders = () => {
    return Object.values(PROVIDER_CONFIGS).filter(config =>
      config.architectureSupport.includes(currentTemplate.architecture)
    );
  };

  const getStatusColor = (status: string) => {
    const normalized = status?.toUpperCase?.() || status;
    switch (normalized) {
      case 'COMPLETED':
        return 'bg-green-500/20 text-green-300';
      case 'FAILED':
        return 'bg-red-500/20 text-red-300';
      case 'CANCELLED':
        return 'bg-yellow-500/20 text-yellow-300';
      case 'BUILDING':
        return 'bg-blue-500/20 text-blue-300';
      case 'AGENTIC_TESTING':
        return 'bg-purple-500/20 text-purple-300';
      case 'VALIDATING':
      case 'TESTING':
        return 'bg-indigo-500/20 text-indigo-300';
      case 'PENDING':
        return 'bg-gray-600/20 text-gray-300';
      default:
        return 'bg-gray-700/20 text-gray-300';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Multi-Cloud Image Builder</h1>
        <p className="text-gray-400">Create and manage custom image templates across AWS, GCP, and Azure</p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-400">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-900/20 border border-green-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess(null)} className="text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Provider Selection */}
      <div className="mb-6">
        <div className="flex items-center space-x-4 mb-4">
          <span className="text-sm font-medium text-gray-300">Active Provider:</span>
          <div className="flex space-x-2">
            {Object.entries(PROVIDER_CONFIGS).map(([key, config]) => (
              <Button
                key={key}
                onClick={() => setSelectedProvider(key as CloudProvider)}
                variant={selectedProvider === key ? 'default' : 'secondary'}
                size="sm"
              >
                {config.displayName}
              </Button>
            ))}
          </div>
        </div>

        {/* Provider Info */}
        <Card>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Image Format:</span>
                <span className="ml-2 text-white">{PROVIDER_CONFIGS[selectedProvider].imageFormat}</span>
              </div>
              <div>
                <span className="text-gray-400">GPU Support:</span>
                <span className="ml-2 text-white">
                  x86_64: {PROVIDER_CONFIGS[selectedProvider].gpuSupport.x86_64 ? 'Yes' : 'No'}
                  {' '} ARM64: {PROVIDER_CONFIGS[selectedProvider].gpuSupport.arm64 ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Architectures:</span>
                <span className="ml-2 text-white">{PROVIDER_CONFIGS[selectedProvider].architectureSupport.join(', ')}</span>
              </div>
              <div>
                <span className="text-gray-400">Regions:</span>
                <span className="ml-2 text-white">{PROVIDER_CONFIGS[selectedProvider].regions.length} available</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-8">
        <div className="border-b border-gray-800">
          <nav className="-mb-px flex space-x-8">
            {([
              { label: 'Templates', icon: Layers },
              { label: 'Build Jobs', icon: Clock },
              { label: 'Create Template', icon: Sparkles },
              { label: 'Coverage Tracker', icon: Monitor },
              { label: 'Deployed Images', icon: Server },
              { label: 'Base Images', icon: Cloud },
            ] as const).map((tab, idx) => (
              <button
                key={tab.label}
                onClick={() => setActiveTab(idx)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 transition-colors ${
                  activeTab === idx
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Active Build Progress Cards */}
      {activeBuilds.length > 0 ? (
        <div className="mb-6 space-y-4">
          {/* What's Happening Explainer — rendered once for all builds */}
          <details className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-3">
            <summary className="text-xs font-semibold text-gray-300 cursor-pointer uppercase tracking-wide hover:text-gray-200">
              What's Happening? (Click to learn)
            </summary>
            <div className="mt-3 space-y-2 text-xs text-gray-400 leading-relaxed">
              <p>
                <strong className="text-gray-300">Pre-Flight Testing:</strong> Before building your full image (20-40 minute process),
                we test all commands in temporary virtual machines. This catches issues in 1-4 minutes instead of discovering them after a long build,
                saving you time and money.
              </p>
              <p>
                <strong className="text-gray-300">AI Auto-Fixing:</strong> If a command fails (wrong path, missing flag, incompatible version),
                AI analyzes the error and suggests a fix. If the fix works when tested, it's applied and saved to your template automatically.
              </p>
              <p>
                <strong className="text-gray-300">Why Multiple Providers:</strong> Testing on both AWS and GCP ensures your image will work
                across clouds. Different providers have subtle differences in base images, package locations, and system configurations.
              </p>
            </div>
          </details>

          {activeBuilds.map(build => (
            <div key={build.key} className="relative">
              {/* Card controls: collapse + dismiss */}
              <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                <button
                  onClick={() => toggleCollapseBuild(build.key)}
                  className="text-gray-400 hover:text-white bg-gray-800/80 rounded-full w-6 h-6 flex items-center justify-center text-xs"
                  title={collapsedBuilds.has(build.key) ? 'Expand' : 'Minimize'}
                >
                  {collapsedBuilds.has(build.key) ? '+' : '\u2013'}
                </button>
                <button
                  onClick={() => dismissBuild(build.key)}
                  className="text-gray-400 hover:text-white bg-gray-800/80 rounded-full w-6 h-6 flex items-center justify-center text-sm"
                  title="Dismiss"
                >
                  x
                </button>
              </div>

              {/* Collapsed summary bar - shown when collapsed */}
              {collapsedBuilds.has(build.key) && (
                <div
                  className="bg-gray-900/30 border border-gray-700 rounded-lg px-3 py-2 cursor-pointer hover:border-gray-600 transition-colors"
                  onClick={() => toggleCollapseBuild(build.key)}
                >
                  <div className="flex items-center gap-2 pr-16">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-300">
                      {build.provider}
                    </span>
                    <span className="text-xs text-gray-400">{build.templateName}</span>
                    {build.jobId > 0 && (
                      <span className="text-xs text-gray-500">Job #{build.jobId}</span>
                    )}
                    <span className="text-xs text-blue-400 animate-pulse ml-auto">Running</span>
                  </div>
                </div>
              )}
              {/* Build card - always mounted to preserve SSE state, hidden when collapsed */}
              <div className={collapsedBuilds.has(build.key) ? 'hidden' : 'pt-4'}>
                <BuildProgressStream
                  templateId={build.templateId}
                  templateName={build.templateName}
                  templateType={build.templateType}
                  providers={[build.provider]}
                  jobIds={build.jobId > 0 ? [build.jobId] : []}
                  onComplete={(success) => {
                    if (success) {
                      setTimeout(() => dismissBuild(build.key), 30000);
                    }
                    loadBuildJobs();
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="mb-6">
          <div className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-400">Build Pipeline</p>
              <p className="text-sm text-gray-500 mt-0.5">
                {buildJobs.length > 0
                  ? `Last build: ${buildJobs[0]?.template_name} - ${buildJobs[0]?.status}`
                  : 'No builds yet'}
              </p>
            </div>
            <Badge variant="secondary">Idle</Badge>
          </div>
        </Card>
      )}

      {/* Templates Tab */}
      {activeTab === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const baseType = template.base_image_type || 'ubuntu';
            const gradientBar: Record<string, string> = {
              rapids: 'from-green-500 to-emerald-600',
              ubuntu: 'from-gray-500 to-gray-600',
            };
            const gradient = template.include_cufolio
              ? 'from-purple-500 to-pink-600'
              : template.gpu_enabled && baseType === 'ubuntu'
              ? 'from-blue-500 to-indigo-600'
              : gradientBar[baseType] || gradientBar.ubuntu;

            return (
              <Card key={template.id} className="group overflow-hidden hover:border-gray-600 transition-all duration-200">
                <div className={`h-1 bg-gradient-to-r ${gradient}`} />
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{template.name}</h3>
                      <p className="text-sm text-gray-400">{template.description}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Badges row */}
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant={baseType === 'rapids' ? 'default' : 'secondary'}
                        className={baseType === 'rapids' ? 'bg-green-600 hover:bg-green-700' : ''}>
                        {baseType.toUpperCase()}
                      </Badge>
                      {template.base_image_type === 'rapids' && template.base_image_version && (
                        <Badge variant="outline" className="border-green-500/30 text-green-300">
                          v{parseRapidsVersion(template.base_image_version)}
                        </Badge>
                      )}
                      <Badge variant="outline" className={
                        template.architecture === 'x86_64'
                          ? 'border-blue-500/30 text-blue-400'
                          : 'border-purple-500/30 text-purple-400'
                      }>
                        {template.architecture}
                      </Badge>
                      {template.include_cufolio && (
                        <Badge variant="outline" className="border-purple-500/30 text-purple-400">
                          cuFolio
                        </Badge>
                      )}
                    </div>

                    <div className="text-sm text-gray-400">
                      Python {template.python_version}
                      {template.gpu_enabled && (
                        <span className="text-green-400 ml-2">
                          GPU {template.cuda_version && `(CUDA ${template.cuda_version})`}
                        </span>
                      )}
                      {template.disk_size_gb && (
                        <span className="ml-2">{template.disk_size_gb}GB disk</span>
                      )}
                    </div>

                    <div className="space-y-1">
                      {template.propalgos_base_packages && (
                        <div className="text-sm text-blue-400 flex items-center">
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Base Packages ({BASELINE_PACKAGE_NAMES.size})
                        </div>
                      )}
                      <div className="text-sm text-gray-400">
                        {template.packages.length} additional packages
                      </div>
                    </div>

                    {/* Multi-Provider Support */}
                    <div className="space-y-2">
                      <div className="text-sm text-gray-400">Target Providers:</div>
                      <div className="flex flex-wrap gap-1">
                        {template.target_providers.map(provider => (
                          <Badge key={provider} variant="secondary" className="text-xs font-normal">
                            {PROVIDER_CONFIGS[provider].displayName}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Region Count by Provider */}
                    <div className="text-sm text-gray-400">
                      {Object.entries(template.target_regions)
                        .filter(([provider]) => provider in PROVIDER_CONFIGS)
                        .map(([provider, regions]) => (
                        <div key={provider} className="flex justify-between">
                          <span>{PROVIDER_CONFIGS[provider as CloudProvider].displayName}:</span>
                          <span>{regions.length} regions</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Button
                      onClick={() => {
                        const safeTemplate = {
                          ...template,
                          packages: template.packages || [],
                          target_providers: template.target_providers || ['aws'],
                          target_regions: template.target_regions || { aws: ['us-east-1'] },
                          architecture: template.architecture || 'x86_64',
                          propalgos_base_packages: template.propalgos_base_packages ?? true
                        };
                        handleStartBuild(safeTemplate);
                      }}
                      className="flex-1"
                      size="sm"
                      title="Start build pipeline: validates packages, tests in VM, then builds images"
                    >
                      <Play className="w-3.5 h-3.5" />
                      Build
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        const safeTemplate = {
                          ...template,
                          packages: template.packages || [],
                          target_providers: template.target_providers || ['aws'],
                          target_regions: template.target_regions || { aws: ['us-east-1'] },
                          architecture: template.architecture || 'x86_64',
                          propalgos_base_packages: template.propalgos_base_packages ?? true
                        };
                        setCurrentTemplate(safeTemplate);
                        setActiveTab(2);
                      }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteTemplate(template.id!, template.name)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}

          {templates.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Layers className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No image templates found</p>
              <Button onClick={() => setActiveTab(2)}>
                <Sparkles className="w-4 h-4" />
                Create Your First Template
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Build Jobs Tab */}
      {activeTab === 1 && (
        <Card className="overflow-hidden">
          {/* Header with Refresh Button */}
          <div className="p-4 border-b border-gray-800 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Build Jobs</h3>
            <Button
              size="sm"
              onClick={() => {
                loadBuildJobs();
                setSuccess('Refreshed build jobs');
              }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
          {buildJobs.length > 0 ? (
            <div className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-8"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Template
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {buildJobs.map((job) => (
                    <React.Fragment key={job.id}>
                      <tr className="hover:bg-gray-700/30 cursor-pointer transition-colors">
                        <td className="px-6 py-4">
                          <button
                            onClick={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                            className="text-gray-400 hover:text-white"
                            aria-label={expandedJobId === job.id ? "Hide build details" : "Show build details"}
                          >
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expandedJobId === job.id ? 'rotate-180' : ''}`} />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">{job.template_name}</div>
                          <div className="text-sm text-gray-400">Job #{job.id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const provider = (job.provider || job.build_config?.provider || 'unknown') as string;
                            const logos: Record<string, string> = {
                              aws: '/icons/icons8-aws-48.svg',
                              gcp: '/icons/icons8-google-cloud-48.svg',
                              azure: '/icons/azure-48.svg',
                            };
                            return (
                              <span className="inline-flex items-center gap-1.5">
                                {logos[provider] && <img src={logos[provider]} alt={provider} className="w-4 h-4" />}
                                <Badge variant="secondary" className="text-xs font-normal">
                                  {PROVIDER_CONFIGS[provider as CloudProvider]?.displayName || provider}
                                </Badge>
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {job.status === 'BUILDING' && buildProgress[job.id] ? (
                            <div className="flex flex-col gap-1 min-w-[200px]">
                              <div className="flex items-center gap-2">
                                <div className="relative">
                                  <div className="h-2 w-2 bg-blue-500 rounded-full" />
                                  <div className="absolute inset-0 h-2 w-2 bg-blue-500 rounded-full animate-ping opacity-75" />
                                </div>
                                <span className="text-xs text-blue-400">{buildProgress[job.id].stage}</span>
                              </div>
                              <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 transition-all duration-500"
                                  style={{ width: `${buildProgress[job.id].progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500">{buildProgress[job.id].progress}%</span>
                            </div>
                          ) : (
                            <Badge className={getStatusColor(job.status)}>
                              {job.status}
                            </Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {job.duration_minutes
                            ? `${job.duration_minutes}m`
                            : job.status === 'BUILDING' && buildProgress[job.id]
                            ? `${buildProgress[job.id].elapsed_minutes}m`
                            : job.status === 'BUILDING'
                            ? '...'
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {job.created_by || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            {/* Cancel button - show for active builds */}
                            {['PENDING', 'VALIDATING', 'AGENTIC_TESTING', 'BUILDING'].includes(job.status) && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleCancelJob(job.id, job.template_id)}
                                title="Cancel build and cleanup resources"
                              >
                                <X className="w-3.5 h-3.5" />
                                Cancel
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => loadBuildLogs(job.id)}
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Logs
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expandedJobId === job.id && (
                        <tr key={`${job.id}-details`} className="bg-gray-700/20">
                          <td colSpan={7} className="px-6 py-4">
                            <div className="space-y-3 text-sm">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <span className="text-gray-400">Created:</span>
                                  <span className="ml-2 text-white">{new Date(job.created_at).toLocaleString()}</span>
                                </div>
                                {job.build_started_at && (
                                  <div>
                                    <span className="text-gray-400">Started:</span>
                                    <span className="ml-2 text-white">{new Date(job.build_started_at).toLocaleString()}</span>
                                  </div>
                                )}
                                {job.build_completed_at && (
                                  <div>
                                    <span className="text-gray-400">Completed:</span>
                                    <span className="ml-2 text-white">{new Date(job.build_completed_at).toLocaleString()}</span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-gray-400">Region:</span>
                                  <span className="ml-2 text-white">{job.build_region}</span>
                                </div>
                              </div>

                              {job.error_message && (
                                <div className="mt-3 p-3 bg-red-900/20 border border-red-800 rounded">
                                  <div className="text-red-400 font-semibold mb-1">Error:</div>
                                  <div className="text-red-300 text-xs font-mono">{job.error_message}</div>
                                </div>
                              )}

                              {/* AI-Powered RCA Analysis Display */}
                              {job.rca_analysis && (
                                <div className="mt-3 p-3 bg-blue-900/20 border border-blue-800 rounded">
                                  <div className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                                    <span className="text-lg">🤖</span>
                                    AI Diagnosis
                                  </div>
                                  <div className="space-y-2 text-sm">
                                    {job.rca_analysis.error_type && (
                                      <div>
                                        <span className="text-gray-400">Error Type:</span>
                                        <span className="ml-2 text-white">{job.rca_analysis.error_type}</span>
                                      </div>
                                    )}
                                    {job.rca_analysis.failed_provisioner && (
                                      <div>
                                        <span className="text-gray-400">Failed At:</span>
                                        <span className="ml-2 text-white">{job.rca_analysis.failed_provisioner}</span>
                                      </div>
                                    )}
                                    {job.rca_analysis.ai_diagnosis && (
                                      <div className="mt-2 p-2 bg-gray-800 rounded text-gray-300 text-xs">
                                        {job.rca_analysis.ai_diagnosis}
                                      </div>
                                    )}
                                    {job.rca_analysis.ai_suggested_fix && (
                                      <div className="mt-2">
                                        <span className="text-gray-400 text-xs">Suggested Fix:</span>
                                        <code className="block mt-1 p-2 bg-gray-900 rounded text-green-400 text-xs overflow-x-auto">
                                          {job.rca_analysis.ai_suggested_fix}
                                        </code>
                                      </div>
                                    )}
                                    {job.rca_analysis.fixes_attempted && job.rca_analysis.fixes_attempted.length > 0 && (
                                      <div className="mt-2">
                                        <span className="text-gray-400 text-xs">Fixes Attempted:</span>
                                        <div className="mt-1 space-y-1">
                                          {job.rca_analysis.fixes_attempted.map((attempt, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-xs">
                                              <span>{attempt.success ? '✅' : '❌'}</span>
                                              <span className="text-gray-400">{attempt.fix}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-700">
                                      {job.rca_analysis.ai_confidence !== undefined && (
                                        <span>Confidence: {Math.round(job.rca_analysis.ai_confidence * 100)}%</span>
                                      )}
                                      {job.rca_analysis.model_used && (
                                        <span className="ml-3">Model: {job.rca_analysis.model_used}</span>
                                      )}
                                      {job.rca_analysis.iteration_count !== undefined && (
                                        <span className="ml-3">Iterations: {job.rca_analysis.iteration_count}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {job.deployed_images && job.deployed_images.length > 0 && (
                                <div className="mt-3">
                                  <div className="text-gray-400 font-semibold mb-2">Deployed Images:</div>
                                  <div className="space-y-1">
                                    {job.deployed_images.map((img, idx) => (
                                      <div key={idx} className="flex items-center gap-3 p-2 bg-gray-800/50 rounded">
                                        <span className="px-2 py-0.5 bg-gray-700 text-xs rounded">{img.provider}</span>
                                        <span className="text-gray-400 text-xs">{img.region}</span>
                                        <span className="text-white font-mono text-xs">{img.image_id}</span>
                                        <span className="text-gray-400 text-xs">{img.image_name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <Clock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No build history</p>
              <p className="text-sm text-gray-500 mt-1">Build a template to see jobs here</p>
            </div>
          )}
        </Card>
      )}

      {/* Create Template Tab */}
      {activeTab === 2 && (
        <Card className="p-6">
          {/* AI Template Generator */}
          <div className="mb-8 p-6 bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-2 border-purple-700 rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">AI Template Generator</h3>
                <p className="text-sm text-gray-400">Describe what you need, AI creates the perfect template</p>
              </div>
            </div>

            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder='Example: "Create template for ML + quant analysis with GPU, using Python 3.12"'
              rows={2}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
            />

            <div className="flex gap-3 mt-3 items-center">
              <select
                value={aiPythonVersion}
                onChange={(e) => setAiPythonVersion(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
              >
                {PYTHON_VERSIONS.map(v => (
                  <option key={v} value={v}>Python {v}</option>
                ))}
              </select>

              <label className="flex items-center gap-2 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm cursor-pointer hover:bg-gray-600">
                <input
                  type="checkbox"
                  checked={aiGpuEnabled}
                  onChange={(e) => setAiGpuEnabled(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="text-white">GPU Enabled</span>
              </label>

              <Button
                onClick={handleGenerateTemplateWithAI}
                disabled={!aiPrompt.trim() || generating}
                className={`flex-1 ${
                  !aiPrompt.trim() || generating
                    ? ''
                    : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-purple-600/30'
                }`}
              >
                {generating ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    AI is generating template...
                  </span>
                ) : (
                  'Generate with AI'
                )}
              </Button>
            </div>

            {aiGeneratedTemplate && (
              <div className="mt-4 p-4 bg-green-900/20 border border-green-800 rounded-lg">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-green-400 mb-2">
                      AI Generated: {aiGeneratedTemplate.name}
                    </h4>
                    <p className="text-gray-300 text-sm mb-3">{aiGeneratedTemplate.description}</p>

                    <div className="mb-3">
                      <p className="text-xs text-gray-400 mb-1">Packages ({aiGeneratedTemplate.packages?.length || 0}):</p>
                      <div className="flex flex-wrap gap-1">
                        {aiGeneratedTemplate.packages?.slice(0, 10).map((pkg: string, i: number) => (
                          <Badge key={i} className="bg-green-900/30 text-green-300 border-green-800/50">
                            {pkg}
                          </Badge>
                        ))}
                        {aiGeneratedTemplate.packages?.length > 10 && (
                          <Badge variant="outline">
                            +{aiGeneratedTemplate.packages.length - 10} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    {aiGeneratedTemplate.rationale && (
                      <details className="text-xs text-gray-400 mt-2">
                        <summary className="cursor-pointer hover:text-gray-300">View AI rationale →</summary>
                        <p className="mt-2 pl-4 border-l-2 border-purple-600 italic">{aiGeneratedTemplate.rationale}</p>
                      </details>
                    )}

                    {aiGeneratedTemplate.use_cases && (
                      <div className="mt-2 text-xs">
                        <p className="text-blue-400 font-medium">Recommended for:</p>
                        <ul className="list-disc list-inside text-gray-400 ml-2">
                          {aiGeneratedTemplate.use_cases.map((uc: string, i: number) => (
                            <li key={i}>{uc}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleUseAITemplate}
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 shadow-lg shadow-green-600/30 whitespace-nowrap"
                  >
                    Use This Template
                  </Button>
                </div>

                {aiGeneratedTemplate.warnings && aiGeneratedTemplate.warnings.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-800">
                    <p className="text-yellow-400 text-xs font-medium mb-1">AI Notes:</p>
                    <ul className="list-disc list-inside text-yellow-300 text-xs space-y-1">
                      {aiGeneratedTemplate.warnings.map((w: string, i: number) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Basic Configuration */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white">Template Configuration</h3>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Template Name</label>
                <input
                  type="text"
                  value={currentTemplate.name}
                  onChange={(e) => setCurrentTemplate(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  placeholder="e.g., ml-workbench-gpu"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={currentTemplate.description}
                  onChange={(e) => setCurrentTemplate(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  placeholder="Brief description of this AMI template..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Architecture</label>
                <select
                  value={currentTemplate.architecture}
                  onChange={(e) => handleArchitectureChange(e.target.value as 'x86_64' | 'arm64')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="x86_64">x86_64 (Intel/AMD)</option>
                  <option value="arm64">ARM64 (Graviton)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Python Version</label>
                <select
                  value={currentTemplate.python_version}
                  onChange={(e) => {
                    const py = e.target.value;
                    setCurrentTemplate(prev => {
                      const updated = { ...prev, python_version: py };
                      if (prev.base_image_type === 'rapids') {
                        const rapidsV = parseRapidsVersion(prev.base_image_version);
                        updated.base_image_version = buildBaseImageVersion(rapidsV, prev.cuda_version || '12.8', py);
                      }
                      return updated;
                    });
                  }}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  {filteredPythonVersions.map(version => {
                    const isUnsupported = currentTemplate.base_image_type === 'rapids'
                      && !RAPIDS_COMPATIBILITY[currentRapidsVersion]?.python.includes(version);
                    return (
                      <option key={version} value={version}>
                        Python {version}{isUnsupported ? ' (unsupported)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Base Image Type</label>
                <select
                  value={currentTemplate.base_image_type || 'ubuntu'}
                  onChange={(e) => {
                    const baseType = e.target.value as 'ubuntu' | 'rapids';
                    if (baseType === 'rapids') {
                      const rapidsV = '24.10';
                      const compat = RAPIDS_COMPATIBILITY[rapidsV];
                      const py = compat.python.includes(currentTemplate.python_version)
                        ? currentTemplate.python_version : compat.python[0];
                      const cuda = (currentTemplate.cuda_version && compat.cuda.includes(currentTemplate.cuda_version))
                        ? currentTemplate.cuda_version : compat.cuda[compat.cuda.length - 1];
                      setCurrentTemplate(prev => ({
                        ...prev,
                        base_image_type: baseType,
                        base_image_version: buildBaseImageVersion(rapidsV, cuda, py),
                        python_version: py,
                        cuda_version: cuda,
                        gpu_enabled: true,
                        include_cufolio: prev.include_cufolio,
                        min_gpu_memory_gb: prev.min_gpu_memory_gb,
                      }));
                    } else {
                      setCurrentTemplate(prev => ({
                        ...prev,
                        base_image_type: baseType,
                        base_image_version: undefined,
                        include_cufolio: false,
                        min_gpu_memory_gb: undefined,
                      }));
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="ubuntu">Ubuntu 22.04 (clean base, install via pip)</option>
                  <option value="rapids">NVIDIA RAPIDS (cuDF, cuML pre-installed)</option>
                </select>

                {currentTemplate.base_image_type === 'rapids' && (
                  <>
                  {/* RAPIDS Version Selector */}
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-300 mb-2">RAPIDS Version</label>
                    <select
                      value={currentRapidsVersion}
                      disabled={currentTemplate.include_cufolio}
                      onChange={(e) => {
                        const rapidsV = e.target.value;
                        const compat = RAPIDS_COMPATIBILITY[rapidsV];
                        if (!compat) return;
                        const py = compat.python.includes(currentTemplate.python_version)
                          ? currentTemplate.python_version : compat.python[0];
                        const cuda = (currentTemplate.cuda_version && compat.cuda.includes(currentTemplate.cuda_version))
                          ? currentTemplate.cuda_version : compat.cuda[compat.cuda.length - 1];
                        setCurrentTemplate(prev => ({
                          ...prev,
                          python_version: py,
                          cuda_version: cuda,
                          base_image_version: buildBaseImageVersion(rapidsV, cuda, py),
                        }));
                      }}
                      className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 ${
                        currentTemplate.include_cufolio ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                    >
                      {RAPIDS_VERSIONS.map(v => (
                        <option key={v} value={v}>RAPIDS {v}</option>
                      ))}
                    </select>
                    {currentTemplate.include_cufolio && (
                      <p className="text-xs text-purple-400 mt-1">Locked to 25.10 by cuFolio</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Compatible: Python {RAPIDS_COMPATIBILITY[currentRapidsVersion]?.python.join(', ') || '?'} | CUDA {RAPIDS_COMPATIBILITY[currentRapidsVersion]?.cuda.join(', ') || '?'}
                    </p>
                  </div>

                  <div className="mt-2 p-3 bg-green-900/20 border border-green-700 rounded-lg">
                    <p className="text-green-300 text-sm font-medium mb-1">Pre-installed RAPIDS Packages:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs text-gray-300">
                      <div>• cuDF {currentRapidsVersion} (GPU DataFrames)</div>
                      <div>• cuML {currentRapidsVersion} (GPU ML)</div>
                      <div>• cuGraph {currentRapidsVersion} (GPU Graphs)</div>
                      <div>• dask-cuDF (distributed)</div>
                      <div>• cuSignal (signal processing)</div>
                      <div>• CUDA {currentTemplate.cuda_version || '12.8'} toolkit</div>
                    </div>
                    <p className="text-gray-400 text-xs mt-2">
                      Add additional packages via pip on top of RAPIDS base
                    </p>

                    {/* cuFolio Toggle */}
                    <div className="mt-3 pt-3 border-t border-green-700/50">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentTemplate.include_cufolio || false}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            if (enabled) {
                              const cufolioCompat = RAPIDS_COMPATIBILITY['25.10'];
                              setCurrentTemplate(prev => {
                                const py = cufolioCompat.python.includes(prev.python_version)
                                  ? prev.python_version : '3.12';
                                const cuda = cufolioCompat.cuda.includes(prev.cuda_version || '')
                                  ? prev.cuda_version! : '12.8';
                                return {
                                  ...prev,
                                  include_cufolio: true,
                                  gpu_enabled: true,
                                  python_version: py,
                                  cuda_version: cuda,
                                  base_image_version: buildBaseImageVersion('25.10', cuda, py),
                                  disk_size_gb: Math.max(prev.disk_size_gb || 100, 150),
                                  min_gpu_memory_gb: 40,
                                };
                              });
                            } else {
                              setCurrentTemplate(prev => ({
                                ...prev,
                                include_cufolio: false,
                                min_gpu_memory_gb: undefined,
                              }));
                            }
                          }}
                          className="mt-1 h-4 w-4 rounded border-purple-500 text-purple-500 focus:ring-purple-500 bg-gray-700"
                        />
                        <div>
                          <span className="text-purple-300 text-sm font-medium">
                            Include cuFolio (GPU Portfolio Optimization)
                          </span>
                          {currentTemplate.include_cufolio && (
                            <div className="mt-2 p-2 bg-purple-900/30 border border-purple-700/50 rounded text-xs">
                              <div className="grid grid-cols-2 gap-1 text-gray-300">
                                <div>• cuOpt 25.10 (optimization)</div>
                                <div>• cuML 25.10 (GPU ML)</div>
                                <div>• cvxpy 1.7.3 (convex opt)</div>
                                <div>• yfinance (market data)</div>
                                <div>• numpy 2.x (upgraded)</div>
                                <div>• scikit-learn 1.7.1</div>
                              </div>
                              <div className="mt-2 flex items-center gap-1 text-yellow-400">
                                <AlertCircle className="w-3.5 h-3.5" />
                                <span>40GB+ VRAM required at runtime</span>
                              </div>
                              <p className="mt-1 text-gray-400">
                                Compatible: A100, H100, L40 &mdash; Forces RAPIDS 25.10 + 150GB disk
                              </p>
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                  </>
                )}

              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={currentTemplate.gpu_enabled}
                    onChange={(e) => handleGpuChange(e.target.checked)}
                    disabled={!canEnableGpu()}
                    className="sr-only"
                  />
                  <div className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                    currentTemplate.gpu_enabled ? 'bg-blue-600' : 'bg-gray-600'
                  } ${!canEnableGpu() ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      currentTemplate.gpu_enabled ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-300">
                    GPU Support {!canEnableGpu() && '(Not available for current architecture/providers)'}
                  </span>
                </label>
              </div>

              {currentTemplate.gpu_enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">CUDA Version</label>
                  <select
                    value={currentTemplate.cuda_version || ''}
                    onChange={(e) => {
                      const cuda = e.target.value;
                      setCurrentTemplate(prev => {
                        const updated = { ...prev, cuda_version: cuda };
                        if (prev.base_image_type === 'rapids') {
                          const rapidsV = parseRapidsVersion(prev.base_image_version);
                          updated.base_image_version = buildBaseImageVersion(rapidsV, cuda, prev.python_version);
                        }
                        return updated;
                      });
                    }}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    {filteredCudaVersions.map(version => {
                      const isUnsupported = currentTemplate.base_image_type === 'rapids'
                        && !RAPIDS_COMPATIBILITY[currentRapidsVersion]?.cuda.includes(version);
                      const isDeprecated = version === '11.8';
                      const suffix = isUnsupported ? ' (unsupported)' : isDeprecated ? ' (deprecated)' : '';
                      return (
                        <option key={version} value={version}>
                          CUDA {version}{suffix}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Disk Size Configuration */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Root Volume Size (GB)</label>
                <div className="flex items-center gap-4">
                  <input
                    type="number"
                    min={50}
                    max={500}
                    step={10}
                    value={currentTemplate.disk_size_gb || 100}
                    onChange={(e) => setCurrentTemplate(prev => ({
                      ...prev,
                      disk_size_gb: Math.max(50, Math.min(500, parseInt(e.target.value) || 100))
                    }))}
                    className="w-32 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                  <div className="flex gap-2">
                    {[50, 100, 150, 200].map(size => (
                      <Button
                        key={size}
                        type="button"
                        variant={currentTemplate.disk_size_gb === size ? 'default' : 'secondary'}
                        size="sm"
                        onClick={() => setCurrentTemplate(prev => ({ ...prev, disk_size_gb: size }))}
                      >
                        {size}GB
                      </Button>
                    ))}
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {currentTemplate.base_image_type === 'rapids'
                    ? 'RAPIDS builds need at least 100GB for conda environments and CUDA'
                    : currentTemplate.gpu_enabled
                      ? 'GPU builds need at least 100GB for drivers and frameworks'
                      : 'Standard builds work well with 50-100GB'}
                </p>
              </div>
            </div>

            {/* PropAlgos Base Packages and Additional Packages */}
            <div className="space-y-6">
              {/* PropAlgos Base Packages Section */}
              <div className="bg-gray-700/50 rounded-lg p-4 border border-blue-500/30">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="propalgos-base-packages"
                    checked={currentTemplate.propalgos_base_packages}
                    onChange={(e) => setCurrentTemplate(prev => ({
                      ...prev,
                      propalgos_base_packages: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="propalgos-base-packages" className="text-sm font-medium text-white cursor-pointer">
                      Include PropAlgos Base Packages
                      <Badge className="ml-2 bg-blue-500/20 text-blue-400 border-blue-500/30">Recommended</Badge>
                    </label>
                    <p className="text-xs text-gray-400 mt-1">
                      Essential packages for the PropAlgos experience across all clouds
                    </p>

                    {currentTemplate.propalgos_base_packages && (
                      <div className="mt-3 space-y-3">
                        <div className="text-xs font-medium text-gray-300">PropAlgos Base Includes:</div>

                        {/* System Tools */}
                        <div className="p-2 bg-gray-800/50 rounded-lg">
                          <div className="text-xs font-medium text-blue-400 mb-1">System Tools</div>
                          <div className="grid grid-cols-2 gap-1 text-xs text-gray-400">
                            <div>• propalgos-status, propalgos-health</div>
                            <div>• propalgos-jupyter, propalgos-help</div>
                            <div>• NVIDIA utilities (nvidia-smi)</div>
                            <div>• Terminal access (SSM/IAP ready)</div>
                          </div>
                        </div>

                        {/* Python Environment */}
                        <div className="p-2 bg-gray-800/50 rounded-lg">
                          <div className="text-xs font-medium text-green-400 mb-1">Python Environment (via Miniforge)</div>
                          <div className="grid grid-cols-2 gap-1 text-xs text-gray-400">
                            <div>• Conda with RAPIDS channel</div>
                            <div>• pip with GPU wheels</div>
                          </div>
                        </div>

                        {/* Pre-installed Packages */}
                        <div className="p-2 bg-gray-800/50 rounded-lg">
                          <div className="text-xs font-medium text-purple-400 mb-1">Pre-installed Python Packages</div>
                          <div className="grid grid-cols-2 gap-1 text-xs text-gray-400">
                            <div>• numpy, pandas, scipy, scikit-learn</div>
                            <div>• matplotlib, seaborn, plotly</div>
                            <div>• jupyterlab, ipywidgets</div>
                            <div>• jupyter-server-proxy</div>
                          </div>
                        </div>

                        {/* GPU Libraries (conditional) */}
                        {currentTemplate.gpu_enabled && (
                          <div className="p-2 bg-gray-800/50 rounded-lg">
                            <div className="text-xs font-medium text-yellow-400 mb-1">GPU Libraries</div>
                            <div className="grid grid-cols-2 gap-1 text-xs text-gray-400">
                              <div>• cuDF, cuML, cuGraph (RAPIDS)</div>
                              <div>• CUDA toolkit</div>
                            </div>
                          </div>
                        )}

                        {/* Storage Connectors */}
                        <div className="p-2 bg-gray-800/50 rounded-lg">
                          <div className="text-xs font-medium text-orange-400 mb-1">Storage Connectors</div>
                          <div className="grid grid-cols-2 gap-1 text-xs text-gray-400">
                            <div>• AWS S3 (s3fs, boto3)</div>
                            <div>• GCP GCS (gcsfs, google-cloud-storage)</div>
                          </div>
                        </div>

                        <div className="text-xs text-blue-400 italic">
                          Provides identical experience across AWS, GCP, and other clouds
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Python Packages Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-gray-300">Additional Python Packages</h4>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowPackageSearch(true)}
                      title="Browse packages by category"
                    >
                      Browse
                    </Button>
                    {currentTemplate.packages.length > 0 && (
                      <Button
                        size="sm"
                        onClick={handleValidatePackages}
                        disabled={validationStatus === 'validating'}
                        className={
                          validationStatus === 'validating'
                            ? ''
                            : validationStatus === 'valid'
                            ? 'bg-green-600 hover:bg-green-700'
                            : validationStatus === 'invalid'
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-purple-600 hover:bg-purple-700'
                        }
                      >
                        {validationStatus === 'validating' ? 'Validating...' :
                         validationStatus === 'valid' ? 'Validated' :
                         validationStatus === 'invalid' ? 'Re-validate' :
                         'Validate Packages'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Live Package Search */}
                <div className="mb-4">
                  <PackageAutocomplete
                    onSelect={(packageName) => {
                      handleAddPackage(packageName);
                      setValidationStatus('none');
                    }}
                    existingPackages={currentTemplate.packages}
                    placeholder="Search packages (e.g., pandas, holoviews, arch)..."
                  />
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {currentTemplate.packages.map((pkg, index) => {
                    const baseName = pkg.split(/[>=<[]/)[0].trim();
                    const isDuplicate = currentTemplate.propalgos_base_packages && BASELINE_PACKAGE_NAMES.has(baseName);
                    return (
                      <div key={index} className={`flex items-center justify-between rounded-lg p-2 ${isDuplicate ? 'bg-yellow-900/20 border border-yellow-700/40' : 'bg-gray-700'}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white">{pkg}</span>
                          {isDuplicate && (
                            <span className="text-xs text-yellow-400">(already in baseline)</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-400 hover:text-red-300"
                          onClick={() => {
                            handleRemovePackage(index);
                            setValidationStatus('none');
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                  {currentTemplate.packages.length === 0 && (
                    <div className="text-sm text-gray-400 text-center py-4">
                      No packages added yet
                    </div>
                  )}
                </div>

                {/* Validation Progress Indicator */}
                {validationStatus === 'validating' && (
                  <div className="mt-4 p-4 bg-blue-900/20 border border-blue-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                      <div>
                        <h5 className="text-blue-400 font-semibold">Validating Package Compatibility...</h5>
                        <p className="text-blue-300 text-sm mt-1">
                          Testing {currentTemplate.packages.length} packages with Python {currentTemplate.python_version}
                          {currentTemplate.gpu_enabled && ' + CUDA support'}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">⏱️ This may take 30-60 seconds</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Validation Failed */}
                {validationStatus === 'invalid' && validationResults && (
                  <div className="mt-4 p-4 bg-red-900/20 border border-red-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <h5 className="text-red-400 font-semibold">Package Validation Failed</h5>
                    </div>

                    {validationResults.errors?.length > 0 && (
                      <div className="space-y-2 mb-3">
                        <p className="text-red-300 font-medium text-sm">Errors:</p>
                        {validationResults.errors.map((err: string, idx: number) => (
                          <div key={idx} className="flex items-start gap-2 bg-red-900/30 p-2 rounded">
                            <span className="text-red-400 text-lg leading-none">•</span>
                            <span className="text-red-300 text-sm flex-1">{err}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Quick-Add Suggestions for Alternatives */}
                    {validationResults.suggestions?.length > 0 && (
                      <div className="space-y-2 mb-3 pt-3 border-t border-red-800">
                        <p className="text-blue-400 font-medium text-sm">Recommended Alternatives:</p>
                        {validationResults.suggestions.map((suggestion: any, idx: number) => (
                          <div key={idx} className="bg-blue-900/20 border border-blue-800 p-3 rounded">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <p className="text-blue-300 text-sm font-medium mb-1">
                                  Replace <code className="bg-red-900/40 px-1.5 py-0.5 rounded text-red-300">{suggestion.remove}</code> with <code className="bg-green-900/40 px-1.5 py-0.5 rounded text-green-300">{suggestion.add}</code>
                                </p>
                                <p className="text-gray-400 text-xs">{suggestion.reason}</p>
                              </div>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                                onClick={() => {
                                  const newPackages = currentTemplate.packages.filter(p => p !== suggestion.remove);
                                  if (!newPackages.includes(suggestion.add)) {
                                    newPackages.push(suggestion.add);
                                  }
                                  setCurrentTemplate(prev => ({ ...prev, packages: newPackages }));
                                }}
                              >
                                Quick Replace
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {validationResults.warnings?.length > 0 && (
                      <div className="space-y-1 mb-3 pt-3 border-t border-red-800">
                        <p className="text-yellow-400 font-medium text-sm">Warnings:</p>
                        {validationResults.warnings.map((warn: string, idx: number) => (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="text-yellow-300 text-sm">{warn}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-red-800">
                      <p className="text-yellow-400 text-sm font-medium">
                        Template cannot be saved until validation passes.
                      </p>
                      <p className="text-gray-400 text-xs mt-1">
                        Remove incompatible packages or change Python version, then validation will re-run automatically.
                      </p>
                    </div>
                  </div>
                )}

                {/* Validation Success */}
                {validationStatus === 'valid' && validationResults && (
                  <div className="mt-4 p-4 bg-green-900/20 border border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <h5 className="text-green-400 font-semibold">Validation Successful</h5>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-green-300">
                        <span>{validationResults.total_requested} packages validated</span>
                      </div>
                      <div className="flex items-center gap-2 text-green-300">
                        <span>Resolved to {validationResults.total_resolved} total packages (including dependencies)</span>
                      </div>

                      {validationResults.resolved_packages?.length > 0 && (
                        <details className="mt-3 pt-3 border-t border-green-800">
                          <summary className="text-blue-400 cursor-pointer hover:text-blue-300 text-sm">
                            View all {validationResults.total_resolved} resolved packages →
                          </summary>
                          <div className="mt-2 max-h-40 overflow-y-auto bg-gray-900 p-3 rounded">
                            <div className="grid grid-cols-2 gap-1">
                              {validationResults.resolved_packages.slice(0, 50).map((pkg: string, i: number) => (
                                <div key={i} className="text-gray-300 text-xs font-mono">{pkg}</div>
                              ))}
                              {validationResults.resolved_packages.length > 50 && (
                                <div className="text-gray-500 text-xs">
                                  ... and {validationResults.resolved_packages.length - 50} more
                                </div>
                              )}
                            </div>
                          </div>
                        </details>
                      )}

                      {validationResults.warnings?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-green-800 space-y-1">
                          <p className="text-yellow-400 font-medium">Warnings:</p>
                          {validationResults.warnings.map((warn: string, idx: number) => (
                            <div key={idx} className="flex items-start gap-2">
                              <span className="text-yellow-300 text-xs">{warn}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI Analysis Section - Full Width */}
          {validationResults?.ai_insights && (
            <div className="mt-8 p-6 bg-purple-900/20 border border-purple-800 rounded-xl">
              <div className="flex items-center gap-2 mb-4">
                <h5 className="text-purple-400 font-semibold text-lg">AI Analysis</h5>
                <Badge className={`font-semibold ${
                  validationResults.ai_insights.severity === 'critical' ? 'bg-red-600 text-white border-red-500' :
                  validationResults.ai_insights.severity === 'high' ? 'bg-orange-600 text-white border-orange-500' :
                  validationResults.ai_insights.severity === 'medium' ? 'bg-yellow-600 text-black border-yellow-500' :
                  'bg-green-600 text-white border-green-500'
                }`}>
                  {validationResults.ai_insights.severity?.toUpperCase()}
                </Badge>
              </div>

              {validationResults.ai_insights.overall_assessment && (
                <p className="text-gray-300 text-sm mb-6 italic bg-purple-900/30 p-4 rounded">
                  "{validationResults.ai_insights.overall_assessment}"
                </p>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* AI Critical Issues */}
                {validationResults.ai_insights.critical_issues?.length > 0 && (
                  <div>
                    <p className="text-red-400 font-semibold text-sm mb-3">Critical Issues Identified by AI:</p>
                    <div className="space-y-2">
                      {validationResults.ai_insights.critical_issues.map((issue: any, i: number) => (
                        <div key={i} className="bg-red-900/30 border border-red-800 p-3 rounded">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-red-300 font-medium text-sm mb-1">
                                {issue.package} {issue.version && `v${issue.version}`}
                              </p>
                              <p className="text-red-200 text-xs mb-2">{issue.issue}</p>
                              <p className="text-green-300 text-xs font-medium">
                                Fix: {issue.fix}
                              </p>
                            </div>
                          </div>
                          {issue.suggested_package && (
                            <Button
                              size="sm"
                              className="mt-2 w-full bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                const newPackages = currentTemplate.packages.map(pkg => {
                                  const basePkg = pkg.split(/[>=<]/)[0];
                                  return basePkg === issue.package ? issue.suggested_package : pkg;
                                });
                                setCurrentTemplate(prev => ({ ...prev, packages: newPackages }));
                                setValidationStatus('none');
                              }}
                            >
                              Auto-Fix
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Recommended Additions */}
                {validationResults.ai_insights.missing_essentials?.length > 0 && (
                  <div>
                    <p className="text-blue-400 font-semibold text-sm mb-3">AI Recommends Adding:</p>
                    <div className="space-y-2">
                      {validationResults.ai_insights.missing_essentials.map((rec: any, i: number) => (
                        <div key={i} className="bg-blue-900/20 border border-blue-800 p-3 rounded">
                          <p className="text-blue-300 text-sm font-medium mb-1">{rec.package}</p>
                          <p className="text-gray-400 text-xs mb-2">{rec.reason}</p>
                          <Button
                            size="sm"
                            className={`w-full ${
                              currentTemplate.packages.includes(rec.package)
                                ? ''
                                : 'bg-green-600 hover:bg-green-700'
                            }`}
                            onClick={() => {
                              if (!currentTemplate.packages.includes(rec.package)) {
                                setCurrentTemplate(prev => ({
                                  ...prev,
                                  packages: [...prev.packages, rec.package]
                                }));
                              }
                            }}
                            disabled={currentTemplate.packages.includes(rec.package)}
                          >
                            {currentTemplate.packages.includes(rec.package) ? 'Added' : 'Add'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Optimizations */}
                {validationResults.ai_insights.optimizations?.length > 0 && (
                  <div>
                    <p className="text-purple-400 font-semibold text-sm mb-3">AI-Suggested Optimizations:</p>
                    <div className="space-y-2">
                      {validationResults.ai_insights.optimizations.map((opt: any, i: number) => (
                        <div key={i} className="bg-purple-900/30 border border-purple-700 p-3 rounded">
                          <p className="text-purple-300 text-sm mb-1">
                            Replace <code className="bg-red-900/40 px-1.5 py-0.5 rounded text-red-300">{opt.current}</code> with <code className="bg-green-900/40 px-1.5 py-0.5 rounded text-green-300">{opt.suggested}</code>
                          </p>
                          <p className="text-gray-400 text-xs mb-2">{opt.benefit}</p>
                          <Button
                            size="sm"
                            className="w-full bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              const newPkgs = currentTemplate.packages.filter(p => p !== opt.current);
                              if (!newPkgs.includes(opt.suggested)) {
                                newPkgs.push(opt.suggested);
                              }
                              setCurrentTemplate(prev => ({ ...prev, packages: newPkgs }));
                            }}
                          >
                            Apply
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Full Image Contents Manifest */}
          <div className="mt-8">
            <details className="bg-gray-700/30 border border-gray-600 rounded-lg">
              <summary className="p-4 cursor-pointer text-sm font-semibold text-gray-300 hover:text-white select-none">
                Full Image Contents — what gets baked into the image
              </summary>
              <div className="px-4 pb-4 space-y-2">
                {[
                  { label: 'System', color: 'text-gray-400', items: 'Ubuntu 22.04, gcc/g++/make, cloud-init', show: true },
                  { label: 'GPU Drivers', color: 'text-yellow-400', items: 'NVIDIA drivers, CUDA toolkit', show: currentTemplate.gpu_enabled },
                  { label: 'Conda', color: 'text-green-400', items: 'Miniforge (conda + mamba), rapids env', show: currentTemplate.base_image_type === 'rapids' },
                  { label: 'RAPIDS', color: 'text-green-300', items: `cuDF, cuML, cuGraph, dask-cuDF, cuSignal (${currentRapidsVersion})`, show: currentTemplate.base_image_type === 'rapids' },
                  { label: 'Baseline Packages', color: 'text-blue-400', items: Array.from(BASELINE_PACKAGE_NAMES).join(', '), show: currentTemplate.propalgos_base_packages },
                  { label: 'PropAlgos SDK', color: 'text-blue-300', items: 'propalgos, pydantic, httpx, typer', show: currentTemplate.propalgos_base_packages },
                  { label: 'Jupyter', color: 'text-orange-400', items: 'JupyterLab + server-proxy, backend integration', show: true },
                  { label: 'Storage', color: 'text-orange-300', items: 'rclone, s3fs/mount-s3, gcsfuse, sync daemon', show: true },
                  { label: 'CLI Tools', color: 'text-cyan-400', items: 'propalgos-status/health/jupyter/help/packages, MOTD', show: currentTemplate.propalgos_base_packages },
                  { label: 'Security', color: 'text-red-400', items: 'Trivy scanner, CSP agent validation', show: true },
                  { label: 'CSP Setup', color: 'text-gray-300', items: 'AWS SSM, GCP IAP, Azure IMDS', show: true },
                  { label: 'cuFolio', color: 'text-purple-400', items: 'cuOpt 25.10, cvxpy, numpy 2.x', show: currentTemplate.include_cufolio },
                  { label: 'User Packages', color: 'text-white', items: currentTemplate.packages.length > 0 ? currentTemplate.packages.join(', ') : '(none)', show: true },
                ].filter(row => row.show).map(row => (
                  <div key={row.label} className="flex items-start gap-3 text-xs">
                    <span className={`${row.color} font-semibold w-32 shrink-0`}>{row.label}</span>
                    <span className="text-gray-400">{row.items}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>

          {/* Target Providers & Regions - Full Width Section */}
          <div className="mt-8">
            <h4 className="text-sm font-medium text-gray-300 mb-4">Target Providers & Regions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {getSupportedProviders().map(config => (
                <div
                  key={config.name}
                  className={`bg-gray-700 rounded-lg p-4 border-2 transition-all duration-200 ${
                    currentTemplate.target_providers.includes(config.name as CloudProvider)
                      ? 'border-blue-500 bg-gray-700/80'
                      : 'border-transparent hover:border-gray-600'
                  }`}
                >
                  <label className="flex items-center justify-between mb-3 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={currentTemplate.target_providers.includes(config.name as CloudProvider)}
                        onChange={(e) => handleProviderChange(config.name as CloudProvider, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-medium text-white">{config.displayName}</span>
                    </div>
                    <Badge className={
                      config.gpuSupport[currentTemplate.architecture]
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : 'bg-gray-600 text-gray-400 border-gray-500'
                    }>
                      GPU: {config.gpuSupport[currentTemplate.architecture] ? 'Yes' : 'No'}
                    </Badge>
                  </label>

                  {currentTemplate.target_providers.includes(config.name as CloudProvider) && (
                    <div className="space-y-2 pl-6">
                      <div className="text-xs font-medium text-gray-400">Regions:</div>
                      <div className="flex flex-wrap gap-1">
                        {config.regions.map(region => (
                          <label
                            key={region}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${
                              currentTemplate.target_regions[config.name]?.includes(region)
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={currentTemplate.target_regions[config.name]?.includes(region) || false}
                              onChange={(e) => {
                                const currentRegions = currentTemplate.target_regions[config.name] || [];
                                const newRegions = e.target.checked
                                  ? [...currentRegions, region]
                                  : currentRegions.filter(r => r !== region);

                                setCurrentTemplate(prev => ({
                                  ...prev,
                                  target_regions: {
                                    ...prev.target_regions,
                                    [config.name]: newRegions
                                  }
                                }));
                              }}
                              className="sr-only"
                            />
                            {region}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {currentTemplate.base_image_type === 'ubuntu' && currentTemplate.target_providers.includes('azure') && (
              <div className="mt-3 p-3 bg-yellow-900/20 border border-yellow-700 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
                <p className="text-yellow-300 text-xs">
                  Ubuntu base builds don't currently support Azure — use RAPIDS or GPU base instead, or remove Azure from targets.
                </p>
              </div>
            )}
          </div>

          {/* Compatible Instances Section */}
          {currentTemplate.gpu_enabled && currentTemplate.target_providers.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-300">Compatible GPU Instance Types</h4>
                {loadingInstances && (
                  <span className="text-xs text-gray-400">Loading...</span>
                )}
              </div>

              {Object.keys(compatibleInstances).length === 0 && !loadingInstances && (
                <div className="bg-gray-700/50 rounded-lg p-4 text-sm text-gray-400 text-center">
                  Select providers and regions above to see compatible GPU instances
                </div>
              )}

              {Object.entries(compatibleInstances).map(([provider, instances]) => (
                <div key={provider} className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-400 uppercase">
                      {PROVIDER_CONFIGS[provider as CloudProvider].displayName}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({instances.length} instance types)
                    </span>
                  </div>

                  {instances.length === 0 && (
                    <div className="bg-gray-700/50 rounded-lg p-3 text-xs text-gray-400">
                      No GPU instances available for selected regions
                    </div>
                  )}

                  {instances.length > 0 && (
                    <Card className="overflow-hidden">
                      <div className="max-h-60 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-800 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-gray-400 font-medium">Instance Type</th>
                              <th className="text-left p-2 text-gray-400 font-medium">GPU Model</th>
                              <th className="text-center p-2 text-gray-400 font-medium">Count</th>
                              <th className="text-center p-2 text-gray-400 font-medium">Memory/GPU</th>
                              <th className="text-center p-2 text-gray-400 font-medium">Total Memory</th>
                              <th className="text-left p-2 text-gray-400 font-medium">Category</th>
                              <th className="text-left p-2 text-gray-400 font-medium">Regions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {instances.slice(0, 10).map((instance) => (
                              <tr
                                key={instance.instance_type}
                                className={`border-t border-gray-600 ${!instance.compatible ? 'bg-yellow-900/20' : ''}`}
                              >
                                <td className="p-2 text-white font-mono">{instance.instance_type}</td>
                                <td className="p-2 text-gray-300">{instance.gpu_model}</td>
                                <td className="p-2 text-center text-gray-300">{instance.gpu_count}x</td>
                                <td className="p-2 text-center text-gray-300">{instance.gpu_memory_per_device_gb}GB</td>
                                <td className="p-2 text-center text-blue-400 font-medium">
                                  {instance.total_gpu_memory_gb}GB
                                </td>
                                <td className="p-2 text-gray-300">
                                  <span className="px-2 py-0.5 bg-gray-600 rounded text-xs">
                                    {instance.semantic_category}
                                  </span>
                                </td>
                                <td className="p-2 text-gray-400">
                                  <div className="flex flex-wrap gap-1">
                                    {instance.available_regions.slice(0, 3).map(region => (
                                      <span key={region} className="text-xs">{region}</span>
                                    ))}
                                    {instance.available_regions.length > 3 && (
                                      <span className="text-xs text-gray-500">
                                        +{instance.available_regions.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {instances.length > 10 && (
                        <div className="bg-gray-800 p-2 text-center text-xs text-gray-400">
                          Showing top 10 of {instances.length} instance types
                        </div>
                      )}
                    </Card>
                  )}

                  {instances.some(i => !i.compatible) && (
                    <div className="mt-2 bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-2">
                      <div className="flex items-start gap-2">
                        <div className="text-xs text-yellow-300">
                          <strong>Architecture Warning:</strong> ARM64 GPU instances are limited.
                          Most GPU instances only support x86_64 architecture.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex gap-4">
            <Button
              size="lg"
              onClick={handleSaveTemplate}
              disabled={loading || (currentTemplate.packages.length > 0 && validationStatus !== 'valid')}
              className={
                loading
                  ? ''
                  : currentTemplate.packages.length > 0 && validationStatus !== 'valid'
                  ? ''
                  : validationStatus === 'valid'
                  ? 'bg-green-600 hover:bg-green-700 shadow-lg shadow-green-600/30'
                  : 'bg-blue-600 hover:bg-blue-700'
              }
              title={
                currentTemplate.packages.length > 0 && validationStatus !== 'valid'
                  ? 'Package validation required before saving'
                  : ''
              }
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </span>
              ) : validationStatus === 'valid' ? (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Save Validated Template
                </span>
              ) : currentTemplate.packages.length > 0 ? (
                'Validate Packages to Save'
              ) : (
                'Save Template'
              )}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => {
                resetCurrentTemplate();
                setError(null);
                setSuccess(null);
              }}
            >
              Reset
            </Button>
          </div>
        </Card>
      )}

      {/* Coverage Tracker Tab */}
      {activeTab === 3 && (
        <AMICoverageTracker onNavigateToCreateTemplate={() => setActiveTab(2)} />
      )}

      {/* Deployed Images Tab */}
      {activeTab === 4 && (
        <DeployedImagesTab />
      )}

      {/* Base Images Tab */}
      {activeTab === 5 && (
        <BaseImageDiscoveryTab />
      )}

      {/* Package Search Dialog */}
      <PackageSearchDialog
        open={showPackageSearch}
        onClose={() => setShowPackageSearch(false)}
        onAddPackage={(packageName) => {
          // Toggle: add if not present, remove if present
          if (currentTemplate.packages.includes(packageName)) {
            setCurrentTemplate(prev => ({
              ...prev,
              packages: prev.packages.filter(pkg => pkg !== packageName)
            }));
          } else {
            setCurrentTemplate(prev => ({
              ...prev,
              packages: [...prev.packages, packageName]
            }));
          }
        }}
        onBulkSelect={(packages) => {
          const newPackages = packages.filter(pkg => !currentTemplate.packages.includes(pkg));
          if (newPackages.length > 0) {
            setCurrentTemplate(prev => ({
              ...prev,
              packages: [...prev.packages, ...newPackages]
            }));
          }
        }}
        architecture={currentTemplate.architecture}
        pythonVersion={currentTemplate.python_version}
        gpuOnly={currentTemplate.gpu_enabled}
        existingPackages={currentTemplate.packages}
      />

      {/* Build Logs Modal */}
      {logsJobId !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card variant="glass" className="max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-white">Build Logs - Job #{logsJobId}</h3>
                <p className="text-sm text-gray-400 mt-1">
                  {buildJobs.find(j => j.id === logsJobId)?.template_name} • {buildJobs.find(j => j.id === logsJobId)?.provider}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setLogsJobId(null);
                  setBuildLogs(null);
                }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div ref={logsContainerRef} className="flex-1 overflow-auto p-6">
              {buildLogs ? (
                hasLogContent ? (
                  <div className="space-y-6 text-left">
                    {renderLogSection('Error Details', buildLogs.error_message)}
                    {renderLogSection('Packer Build Logs', buildLogs.packer_logs)}
                    {renderLogSection('VM Test Logs', buildLogs.vm_test_logs)}
                    {renderLogSection(
                      `SSM Document Output${buildLogs.document_name ? ` (${buildLogs.document_name})` : ''}`,
                      buildLogs.ssm_document_output
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-400">
                      {buildLogs.message || 'Logs are not yet available. The build may still be starting up.'}
                    </div>
                  </div>
                )
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-400">Loading logs...</div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    if (logsJobId !== null) {
                      loadBuildLogs(logsJobId);
                    }
                  }}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Refresh Logs
                </Button>
                {(() => {
                  const job = buildJobs.find(j => j.id === logsJobId);
                  const isInProgress = job && ['BUILDING', 'VALIDATING', 'AGENTIC_TESTING', 'COPYING', 'TESTING', 'PENDING'].includes(job.status);
                  return isInProgress ? (
                    <span className="flex items-center gap-2 text-sm text-green-400">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      Auto-refreshing every 5s
                    </span>
                  ) : null;
                })()}
              </div>
              <Button
                size="sm"
                disabled={!combinedLogs}
                onClick={() => {
                  if (!combinedLogs) return;
                  const blob = new Blob([combinedLogs], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `build-job-${logsJobId ?? 'unknown'}-logs.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Download Logs
              </Button>
            </div>
          </Card>
        </div>
      )}

    </div>
  );
}
